import React from "react";
import styles from "./HowToUse.module.css";
import { useTranslation } from "react-i18next";
import tutorialVideoMetadata from "../tutorialVideoMetadata.json";

type StepGroup = "setup" | "generation";

type StepItem = {
  id: string;
  group: StepGroup;
  index: number;
  navLabelKey: string;
  titleKey: string;
  bodyKey: string;
  videoIdByLang?: Record<string, string>;
  modeVideos?: Array<{
    labelKey: string;
    videoIdByLang: Record<string, string>;
  }>;
};

type LocalizedVideoIds = Record<string, string>;

type TutorialVideo = {
  videoId: string;
  name: string;
  uploadDate: string;
};

type TutorialVideoSet = {
  language: string;
  shorts: TutorialVideo;
  longform: TutorialVideo;
};

const TOP_TUTORIAL_VIDEOS: Record<string, TutorialVideoSet> = tutorialVideoMetadata;

const TUTORIAL_LOCALE_ALIASES: Record<string, string> = {
  en: "en-us",
  de: "de-de",
  "es-mx": "es-es",
  fr: "fr-fr",
  hi: "hi-in",
  ja: "ja-jp",
  ko: "ko-kr",
  "pt-pt": "pt-br",
  "zh-tw": "zh-cn",
};

const TUTORIAL_VIDEO_IDS = {
  templateCreate: {
    "en-us": "Elc8JwlxicY",
    "de-de": "Nwd7PDMp1E8",
    "es-es": "CI--AHYCNos",
    "fr-fr": "q5mDqnnGue4",
    "hi-in": "AxYm-2XqBLc",
    "ja-jp": "02y96jy4uIA",
    "ko-kr": "rs7T5JrMFQE",
    "pt-br": "M6kMuZayi-E",
    "zh-cn": "XwlOjcIYPDM",
  },
  thumbnailTemplateLongform: {
    "en-us": "pDPgZDvDSws",
    "de-de": "4Ob0VJhXlgk",
    "es-es": "A-fju6Jg_hw",
    "fr-fr": "EaAaY--flaU",
    "hi-in": "1ZoWRVmrdJA",
    "ja-jp": "fwOpILrlY_I",
    "ko-kr": "IAMMB0mSGk8",
    "pt-br": "M7R0mQWg8Vw",
    "zh-cn": "49armS0JgPg",
  },
  scriptTopic: {
    "en-us": "EVM3dZwvWd0",
    "de-de": "DWYhfnFC5v0",
    "es-es": "VJxBeQvgncQ",
    "fr-fr": "5NrKRj_KjYE",
    "hi-in": "o3ER2OjUxek",
    "ja-jp": "-ObK1dTyvdE",
    "ko-kr": "A0ucNNV1i5E",
    "pt-br": "eK65TQQNG78",
    "zh-cn": "U-bPXnK2yAQ",
  },
  scriptRaw: {
    "en-us": "ul3NQ83eorM",
    "de-de": "3lVxGSCeR4E",
    "es-es": "YIbG8yeFPJo",
    "fr-fr": "3LrXf1PPL4s",
    "hi-in": "ek47J9q6zuc",
    "ja-jp": "XCrMFBbrRHo",
    "ko-kr": "NWcHlre8gz0",
    "pt-br": "PQWanyvdRFo",
    "zh-cn": "EiCC8nFlX00",
  },
  applyTemplate: {
    "en-us": "cZ9zylK2GX4",
    "de-de": "STST7-RKBZM",
    "es-es": "RCYBQ1GK7zw",
    "fr-fr": "qCGG9-m9EhA",
    "hi-in": "QviMbXjVbZo",
    "ja-jp": "CY0P93OD8Kc",
    "ko-kr": "AULnggNvqdM",
    "pt-br": "XWKlGdM7Rz0",
    "zh-cn": "5x90UImRUUI",
  },
  transitionSfx: {
    "en-us": "0CktC3DFhPs",
    "de-de": "LB4zY8Cj2UQ",
    "es-es": "ofWl1jIcKqw",
    "fr-fr": "azQst2jLO8c",
    "hi-in": "Vwx3JuZQAlQ",
    "ja-jp": "v7aRoBjjNGY",
    "ko-kr": "D6X6_d6aJss",
    "pt-br": "bJrCfHJNibI",
    "zh-cn": "5RyTp2aBjp8",
  },
  bgm: {
    "en-us": "zbtlI5Gv8N8",
    "de-de": "tvuUI1ZHKAg",
    "es-es": "fimRgVIGqkg",
    "fr-fr": "EfESWs7GBs8",
    "hi-in": "uyP5s1uQ9KE",
    "ja-jp": "2GMuqv16Ezs",
    "ko-kr": "a3d8KwoHt08",
    "pt-br": "0yPTgWtZ0Cc",
    "zh-cn": "Y9lq2zKQjjI",
  },
  voice: {
    "en-us": "RoB5c8nJX6M",
    "de-de": "1MRhUeCAi9M",
    "es-es": "A1urUJH3RNI",
    "fr-fr": "AzvWh4rMiBc",
    "hi-in": "yncksJvU1RY",
    "ja-jp": "ts-yq3uoiWM",
    "ko-kr": "-T_0WS2Bekk",
    "pt-br": "p5T1J4f3JnM",
    "zh-cn": "rZAxMH6Di9k",
  },
  imageMotion: {
    "en-us": "Y3ongtczQJk",
    "de-de": "Px8TN5elcgU",
    "es-es": "DT2HGrtrTTQ",
    "fr-fr": "ANoApr-QaIc",
    "hi-in": "b1BJXGSP3AM",
    "ja-jp": "vr07YMsziIY",
    "ko-kr": "TyzYbKgqD2Y",
    "pt-br": "MzN35UlmpzY",
    "zh-cn": "D0g9WjJGwo8",
  },
  characterRender: {
    "en-us": "-Wjyuy3CLn0",
    "de-de": "ojzx_A4Mn2w",
    "es-es": "dS0GBur7jWU",
    "fr-fr": "eIer6wC5hi4",
    "hi-in": "jqfB3ilDEEQ",
    "ja-jp": "-B66ooyCVQ4",
    "ko-kr": "0XKCKo1Fhb8",
    "zh-cn": "RBex8TPvbh0",
  },
} satisfies Record<string, LocalizedVideoIds>;

const normalizeTutorialLocale = (languageCode?: string): string => {
  const normalized = (languageCode ?? "en-US").trim().replace(/_/g, "-").toLowerCase();
  return TUTORIAL_LOCALE_ALIASES[normalized] ?? normalized;
};

const getLocalizedTopTutorials = (languageCode?: string): TutorialVideoSet => {
  const normalizedLocale = normalizeTutorialLocale(languageCode);
  return TOP_TUTORIAL_VIDEOS[normalizedLocale] ?? TOP_TUTORIAL_VIDEOS["en-us"];
};

const getLocalizedVideoId = (
  videoIdByLang: LocalizedVideoIds | undefined,
  languageCode: string
): string | undefined => {
  if (!videoIdByLang) {
    return undefined;
  }

  const normalizedLocale = normalizeTutorialLocale(languageCode);
  return (
    videoIdByLang[normalizedLocale] ??
    videoIdByLang["en-us"] ??
    videoIdByLang.en
  );
};

const HowToUse: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const localizedTopTutorials = getLocalizedTopTutorials(lang);

  const topTutorials: Array<{
    id: string;
    title: string;
    video: TutorialVideo;
  }> = [
    {
      id: "auto-video-generation",
      title: t("how_to_use.top_videos.shorts.title", {
        defaultValue: "쇼츠 만들기 영상",
      }),
      video: localizedTopTutorials.shorts,
    },
    {
      id: "longform-studio",
      title: t("how_to_use.top_videos.longform.title", {
        defaultValue: "롱폼 만들기 영상",
      }),
      video: localizedTopTutorials.longform,
    },
  ];

  const steps: StepItem[] = [
    {
      id: "shorts-template",
      group: "setup",
      index: 1,
      navLabelKey: "how_to_use.steps.template_create.nav",
      titleKey: "how_to_use.steps.template_create.title",
      bodyKey: "how_to_use.steps.template_create.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.templateCreate,
    },
    {
      id: "longform-thumbnail-template",
      group: "setup",
      index: 2,
      navLabelKey: "how_to_use.steps.thumbnail_template_longform.nav",
      titleKey: "how_to_use.steps.thumbnail_template_longform.title",
      bodyKey: "how_to_use.steps.thumbnail_template_longform.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.thumbnailTemplateLongform,
    },
    {
      id: "script-generate",
      group: "generation",
      index: 1,
      navLabelKey: "how_to_use.steps.script_generate.nav",
      titleKey: "how_to_use.steps.script_generate.title",
      bodyKey: "how_to_use.steps.script_generate.body",
      modeVideos: [
        {
          labelKey: "how_to_use.steps.script_generate.modes.topic",
          videoIdByLang: TUTORIAL_VIDEO_IDS.scriptTopic,
        },
        {
          labelKey: "how_to_use.steps.script_generate.modes.raw",
          videoIdByLang: TUTORIAL_VIDEO_IDS.scriptRaw,
        },
      ],
    },
    {
      id: "apply-template",
      group: "generation",
      index: 2,
      navLabelKey: "how_to_use.steps.apply_template.nav",
      titleKey: "how_to_use.steps.apply_template.title",
      bodyKey: "how_to_use.steps.apply_template.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.applyTemplate,
    },
    {
      id: "transition-sfx",
      group: "generation",
      index: 3,
      navLabelKey: "how_to_use.steps.transition_sfx.nav",
      titleKey: "how_to_use.steps.transition_sfx.title",
      bodyKey: "how_to_use.steps.transition_sfx.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.transitionSfx,
    },
    {
      id: "bgm",
      group: "generation",
      index: 4,
      navLabelKey: "how_to_use.steps.bgm.nav",
      titleKey: "how_to_use.steps.bgm.title",
      bodyKey: "how_to_use.steps.bgm.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.bgm,
    },
    {
      id: "voice",
      group: "generation",
      index: 5,
      navLabelKey: "how_to_use.steps.voice.nav",
      titleKey: "how_to_use.steps.voice.title",
      bodyKey: "how_to_use.steps.voice.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.voice,
    },
    {
      id: "image-motion",
      group: "generation",
      index: 6,
      navLabelKey: "how_to_use.steps.image_motion.nav",
      titleKey: "how_to_use.steps.image_motion.title",
      bodyKey: "how_to_use.steps.image_motion.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.imageMotion,
    },
    {
      id: "character-render",
      group: "generation",
      index: 7,
      navLabelKey: "how_to_use.steps.character_render.nav",
      titleKey: "how_to_use.steps.character_render.title",
      bodyKey: "how_to_use.steps.character_render.body",
      videoIdByLang: TUTORIAL_VIDEO_IDS.characterRender,
    },
  ];

  const setupSteps = steps.filter((step) => step.group === "setup");
  const generationSteps = steps.filter((step) => step.group === "generation");

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1>{t("how_to_use.hero.title")}</h1>
        <p className={styles.heroSubtitle}>{t("how_to_use.hero.subtitle")}</p>
      </section>

      <section className={styles.mainVideoSection}>
        <div className={styles.topVideosGrid}>
          {topTutorials.map((video) => {
            return (
              <article key={video.id} className={styles.topVideoCard}>
                <h3 className={styles.topVideoTitle}>{video.title}</h3>
                <div className={styles.videoWrapper}>
                  <iframe
                    src={`https://www.youtube.com/embed/${video.video.videoId}`}
                    title={video.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </article>
            );
          })}
        </div>
        <p className={styles.videoNote}>
          {t("how_to_use.top_videos.note", {
            defaultValue: "쇼츠와 롱폼 제작 흐름을 각각 확인한 뒤, 아래 단계로 진행해 주세요.",
          })}
        </p>
      </section>

      <section className={styles.processSummarySection}>
        <p className={styles.processSummary}>
          {t("how_to_use.process_summary", {
            defaultValue:
              "Video creation has 7 steps. Register templates once and reuse them.",
          })}
        </p>
      </section>

      <section className={styles.stepNavSection}>
        <h2 className={styles.stepNavMainTitle}>{t("how_to_use.step_nav.title")}</h2>

        <div className={styles.navGroup}>
          <div className={styles.groupHeader}>
            <h3 className={styles.stepNavTitle}>
              {t("how_to_use.nav_groups.setup.title", {
                defaultValue: "사전 준비",
              })}
            </h3>
            <span className={`${styles.groupBadge} ${styles.oneTimeBadge}`}>
              {t("how_to_use.nav_groups.setup.badge", {
                defaultValue: "최초 1회",
              })}
            </span>
          </div>
          <div className={styles.stepNavGrid}>
            {setupSteps.map((step) => (
              <button
                key={step.id}
                type="button"
                className={styles.stepNavButton}
                onClick={() => scrollToSection(step.id)}
              >
                <span className={styles.stepNavIndex}>{step.index}</span>
                <span className={styles.stepNavLabel}>{t(step.navLabelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.navGroup}>
          <div className={styles.groupHeader}>
            <h3 className={styles.stepNavTitle}>
              {t("how_to_use.nav_groups.generation.title", {
                defaultValue: "영상 생성",
              })}
            </h3>
            <span className={`${styles.groupBadge} ${styles.repeatBadge}`}>
              {t("how_to_use.nav_groups.generation.badge", {
                defaultValue: "매번 7단계",
              })}
            </span>
          </div>
          <div className={styles.stepNavGrid}>
            {generationSteps.map((step) => (
              <button
                key={step.id}
                type="button"
                className={styles.stepNavButton}
                onClick={() => scrollToSection(step.id)}
              >
                <span className={styles.stepNavIndex}>{step.index}</span>
                <span className={styles.stepNavLabel}>{t(step.navLabelKey)}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.stepsSection}>
        <div className={styles.stepGroup}>
          <div className={styles.groupHeader}>
            <h3 className={styles.stepGroupTitle}>
              {t("how_to_use.nav_groups.setup.title", {
                defaultValue: "사전 준비",
              })}
            </h3>
            <span className={`${styles.groupBadge} ${styles.oneTimeBadge}`}>
              {t("how_to_use.nav_groups.setup.badge", {
                defaultValue: "최초 1회",
              })}
            </span>
          </div>
          {setupSteps.map((step) => {
            const stepVideoId = getLocalizedVideoId(step.videoIdByLang, lang);
            const modeVideos =
              step.modeVideos
                ?.map((modeVideo) => ({
                  label: t(modeVideo.labelKey),
                  videoId: getLocalizedVideoId(modeVideo.videoIdByLang, lang),
                }))
                .filter(
                  (modeVideo): modeVideo is { label: string; videoId: string } =>
                    Boolean(modeVideo.videoId)
                ) ?? [];

            return (
              <article key={step.id} id={step.id} className={styles.stepCard}>
                <div className={styles.stepHeader}>
                  <div className={styles.stepIndexCircle}>{step.index}</div>
                  <h4 className={styles.stepTitle}>{t(step.titleKey)}</h4>
                </div>

                {modeVideos.length > 0 ? (
                  <div className={styles.modeVideosGrid}>
                    {modeVideos.map((modeVideo, modeIndex) => (
                      <div key={`${step.id}-mode-${modeIndex}`} className={styles.modeVideoCard}>
                        <h5 className={styles.modeVideoTitle}>{modeVideo.label}</h5>
                        <div className={styles.modeVideoWrapper}>
                          <iframe
                            src={`https://www.youtube.com/embed/${modeVideo.videoId}`}
                            title={`${t(step.titleKey)} - ${modeVideo.label}`}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : stepVideoId ? (
                  <div className={styles.stepVideoWrapper}>
                    <iframe
                      src={`https://www.youtube.com/embed/${stepVideoId}`}
                      title={`Setup Step ${step.index} tutorial`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : null}

                <p className={styles.stepBody}>{t(step.bodyKey)}</p>
              </article>
            );
          })}
        </div>

        <div className={styles.stepGroup}>
          <div className={styles.groupHeader}>
            <h3 className={styles.stepGroupTitle}>
              {t("how_to_use.nav_groups.generation.title", {
                defaultValue: "영상 생성",
              })}
            </h3>
            <span className={`${styles.groupBadge} ${styles.repeatBadge}`}>
              {t("how_to_use.nav_groups.generation.badge", {
                defaultValue: "매번 7단계",
              })}
            </span>
          </div>
          {generationSteps.map((step) => {
            const stepVideoId = getLocalizedVideoId(step.videoIdByLang, lang);
            const modeVideos =
              step.modeVideos
                ?.map((modeVideo) => ({
                  label: t(modeVideo.labelKey),
                  videoId: getLocalizedVideoId(modeVideo.videoIdByLang, lang),
                }))
                .filter(
                  (modeVideo): modeVideo is { label: string; videoId: string } =>
                    Boolean(modeVideo.videoId)
                ) ?? [];

            return (
              <article key={step.id} id={step.id} className={styles.stepCard}>
                <div className={styles.stepHeader}>
                  <div className={styles.stepIndexCircle}>{step.index}</div>
                  <h4 className={styles.stepTitle}>{t(step.titleKey)}</h4>
                </div>

                {modeVideos.length > 0 ? (
                  <div className={styles.modeVideosGrid}>
                    {modeVideos.map((modeVideo, modeIndex) => (
                      <div key={`${step.id}-mode-${modeIndex}`} className={styles.modeVideoCard}>
                        <h5 className={styles.modeVideoTitle}>{modeVideo.label}</h5>
                        <div className={styles.modeVideoWrapper}>
                          <iframe
                            src={`https://www.youtube.com/embed/${modeVideo.videoId}`}
                            title={`${t(step.titleKey)} - ${modeVideo.label}`}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : stepVideoId ? (
                  <div className={styles.stepVideoWrapper}>
                    <iframe
                      src={`https://www.youtube.com/embed/${stepVideoId}`}
                      title={`Generation Step ${step.index} tutorial`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : null}

                <p className={styles.stepBody}>{t(step.bodyKey)}</p>
              </article>
            );
          })}
        </div>
      </section>

    </div>
  );
};

export default HowToUse;
